import { Avatar, Box, Button, Grid, Group, Modal, SimpleGrid, Stack, Table, Text } from "@mantine/core"
import { ArrowsClockwiseIcon, CaretLeftIcon, CheckIcon, ImageIcon } from "@phosphor-icons/react";
import React, { ReactNode, useEffect, useState } from "react"
import useSwr from "swr"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router"
import { useTranslation } from "react-i18next"
import { DisabledReason, HelpButtonGroup, HelpIcon, StatusStepPopover, useHelpContext, useStatusHelp } from "@dehub/theme"
import { InvoiceApi, NetworkApi, OrderApi } from "../../apis"
import { IAuth } from "../../store/Auth"
import { IGeneral } from "../../store/General"
import { currencyFormat, termDiscountSuffix } from "../../utils"
import { dateFormat, dateTimeSecFormat } from "../../utils/date"
import { renderBusinessName } from "../../utils/render-business-name"
import { renderName } from "../../utils/render-name"
import { Dialog } from "../../utils/confirm-modal"
import { message } from "../../utils/message"
import { buildOrderStatusSteps } from "../../utils/order-status-steps"
import { usePermission } from "../permission"
import { ReasonTooltip } from "../ui"
import { Dot, type Tone } from "../delivery/atoms"
import { translateBackendError } from "../delivery/translate-error"
import { DeliveryDateChangeRequestModal } from "../buyer-app/DeliveryDateChangeRequestModal"
import { OrderReturnEntry } from "../buyer-app/returns"
import { BUYER_RETURNS_ROUTES, isOrderReturnable } from "../buyer-app/returns/constants"
import type { IReturnOrderDetail } from "../../apis/return"
import { DeliveryLocationMap, isDeliveryLocationEditable } from "../order/delivery-location-map"
import { DeliveryTimeline } from "../buyer-app/DeliveryTimeline"
import { EDITABLE_ORDER_STATUSES } from "../buyer-sales-list/order-row-actions"
import { BuyerSalesDynamicConfirmForm } from "./confirm-form"
import { BuyerSalesPaymentForm } from "./payment-form"
import { BuyerSalesDynamicReviewForm } from "./review-form"
import { OrderStatusPill } from "./order-status-pill"
import { orderSupplier, orderSupplierId, reorderBlockedReason, useReorder } from "./reorder"
import classes from "./detail-form.module.css"
import pillClasses from "./product-list/catalog.module.css"
import { promoIcon } from "./product-list/price-labels"

type Props = {
  action: any[]
  reload?: () => void
  /**
   * `sales` = /buyer-sales/detail/:id (canvas OrdB_OrderDetail: actions, stock, history);
   * `app` = /buyer-app/orders/:orderId (canvas OrdB_AppOrder: the delivered order with its return section).
   */
  variant?: "sales" | "app"
}

/** @dehub/help namespace of the buyer order detail (sections, actions, reasons). */
const HELP = "order.buyer-sales-detail"

/** Why the order cannot be returned yet (B63): the server sends no window until delivery. */
export const returnBlockedReason = (order: any, t: (key: string) => string): string | null => {
  if (isOrderReturnable(order)) return null
  return order?.returnWindowExpiresAt ? t("buyerApp:entry.windowExpired") : t("buyerApp:entry.notDelivered")
}

const stockOf = (line: any) =>
  (line?.inventories || []).reduce((sum: number, inv: any) => sum + (Number(inv?.availableQuantity) || 0), 0)

function Card({ title, extra, children, flush }: Readonly<{ title?: ReactNode; extra?: ReactNode; children: ReactNode; flush?: boolean }>) {
  return (
    <Box className={classes.card} data-flush={flush || undefined}>
      {(title || extra) && (
        <Group justify="space-between" wrap="nowrap" className={classes.cardHead}>
          {title && <Text className={classes.cardTitle}>{title}</Text>}
          {extra}
        </Group>
      )}
      {children}
    </Box>
  )
}

function Row({ label, value, strong }: Readonly<{ label: ReactNode; value: ReactNode; strong?: boolean }>) {
  return (
    <Group justify="space-between" wrap="nowrap" gap="md" className={classes.kv}>
      <Text className={classes.kvLabel}>{label}</Text>
      <Text className={strong ? classes.kvStrong : classes.kvValue} ta="right">
        {value === undefined || value === null || value === "" ? "-" : value}
      </Text>
    </Group>
  )
}

/** `code` = @dehub/help reason code for `order.buyer-sales-detail.reason.{key}` (R4). */
type ActionTile = { key: string; label: string; allowed: boolean; reason: string; code?: string | null; onClick: () => void; primary?: boolean }

export function BuyerSalesDetailForm({ action, reload, variant = "sales" }: Props) {
  const navigate = useNavigate()
  const { t } = useTranslation(["order", "common", "buyerApp"])
  const [actions, setActions] = React.useState<string[]>([])
  const [dateChangeOpened, setDateChangeOpened] = useState(false)
  const { user } = useSelector((state: { auth: IAuth }) => state.auth)
  const { orderStatus, orderDeliveryStatus } = useSelector((state: { general: IGeneral }) => state.general)
  const ORD_SPLIT = usePermission("ORD_SPLIT")
  const ORD_SAVE = usePermission("ORD_SAVE")
  const ORD_SEND = usePermission("ORD_SEND")
  const ORD_CANCEL = usePermission("ORD_CANCEL")
  const RETURN_CREATE = usePermission("RETURN_CREATE")
  const { addOrder } = useReorder()

  const order = action[1]
  const lines: any[] = order?.lines || []
  const isApp = variant === "app"
  // B40: «Дахин захиалах» names an inactive network up front (useReorder checks again on click).
  const reorderSupplierId = isApp ? "" : orderSupplierId(order)
  const { data: reorderNetwork } = useSwr(
    reorderSupplierId ? ["buyer-sales.detail.network", reorderSupplierId] : null,
    () => NetworkApi.get(reorderSupplierId).catch(() => null),
    { revalidateOnFocus: false },
  )
  const status: string = order?.orderStatus || ""
  const { steps, currentStatus } = buildOrderStatusSteps(order, orderStatus, { perspective: "sent", deliveryRefs: orderDeliveryStatus })
  const currentIndex = steps.findIndex((s) => s.code === currentStatus)
  const canEditGps = isDeliveryLocationEditable(order?.orderStatus, order?.deliveredDate) && (ORD_SAVE.hasAccess || ORD_SEND.hasAccess)

  // Which side am I: PURCHASE = I sent it, SALES = the supplier sent it to me (the
  // session's business id is used when present; /me can return it null).
  const myBusinessId = user?.currentBusinessId ?? user?.currentBusiness?.id
  const isSender = myBusinessId ? myBusinessId === order?.senderBusiness?.id : order?.type === "PURCHASE"
  const isReceiver = myBusinessId ? myBusinessId === order?.receiverBusiness?.id : order?.type === "SALES"
  // B55: the seller is the receiver of a PURCHASE order but the sender of a SALES order.
  const supplier = orderSupplier(order)

  // @dehub/help: the effective status drives the drawer's «Одоо» step; `side` is set only when the supplier sent
  // the order to the buyer (BUYER + RECEIVER rows), else the BUYER default rows apply. The app variant is another screen.
  const { set: setHelpContext } = useHelpContext()
  const stepHelp = useStatusHelp("order.status")
  useEffect(() => {
    if (isApp) return
    setHelpContext({ status: currentStatus || undefined, side: isReceiver && !isSender ? "RECEIVER" : undefined })
  }, [isApp, currentStatus, isReceiver, isSender, setHelpContext])

  // The invoice: the main one once issued, else the advance (proforma) invoice.
  const invoiceId = order?.invoiceId || order?.proformaInvoiceId
  const { data: invoice } = useSwr(invoiceId ? `/inv/invoice/${invoiceId}` : null, async () => await InvoiceApi.get(invoiceId), {
    revalidateOnFocus: false,
  })
  const { data: proforma } = useSwr(
    order?.proformaInvoiceId ? `/inv/invoice/${order.proformaInvoiceId}` : null,
    async () => await InvoiceApi.get(order.proformaInvoiceId),
    { revalidateOnFocus: false },
  )
  const isPaid = proforma?.paymentStatus === "CLOSED" || proforma?.invoiceStatus === "CLOSED"

  const totalQuantity = lines.reduce((sum: number, item: any) => sum + (Number(item?.quantity) || 0), 0)
  const orderAmount = Number(order?.orderAmount) || 0
  const shippingAmount = Number(order?.shippingAmount) || 0
  const totalAmount = Number(order?.totalAmount) || 0
  // B56: the header discount is whatever separates the persisted figures
  // (total = order amount - discount + shipping), on SALES orders as well. On a
  // PURCHASE order it is the payment-term rule the server applied; on a SALES
  // order it is the supplier's own header discount, so it is not labelled a term rule.
  const headerDiscount = order?.discountType ? Math.max(0, orderAmount + shippingAmount - totalAmount) : 0
  const discountLabel = `${order?.type === "PURCHASE" ? t("order:buyerSales.termDiscountApplied") : t("order:buyerSales.discount")}${termDiscountSuffix({
    discountType: order?.discountType,
    discountValue: order?.discountValue || 0,
  })}`

  // ── Actions (canvas OrdB_OrderDetail «Үйлдэл»): each stays visible with its reason ──
  const canEditPerm = ORD_SAVE.hasAccess || ORD_SEND.hasAccess
  const editable = isSender && EDITABLE_ORDER_STATUSES.includes(status)
  const canReview = status === "REGISTERED" && isSender
  const canApprove = status === "REVIEWED" && isReceiver
  const delivering = ["DELIVERING", "DELIVERED"].includes(currentStatus)
  const dateChangeStatus = ["REVIEWED", "APPROVED", "AUTHORIZED", "PREPARING"].includes(status) && !delivering
  const cancellable = isSender && status === "REVIEWED"
  const returnReason = returnBlockedReason(order, t)
  const noPerm = t("common:permission.required")

  const handleCancel = () =>
    Dialog.confirm(t("order:buyerSales.cancelConfirm"), async (key) => {
      if (key !== "confirm" || !order?.id) return
      try {
        await OrderApi.cancel(order.id)
        message.success(t("order:buyerSales.cancelSuccess"))
        reload?.()
      } catch (err) {
        message.error(translateBackendError(err))
      }
    })

  const tiles: ActionTile[] = [
    {
      key: "edit",
      label: t("order:actions.edit"),
      allowed: editable && canEditPerm,
      reason: !canEditPerm ? noPerm : editable ? t("order:buyerOrders.detail.editOk") : t("order:buyerOrders.detail.editBlocked"),
      code: !canEditPerm ? "PERM" : editable ? null : "NOT_IN_STATUS",
      onClick: () => navigate(`/buyer-sales/edit/${order.id}`),
    },
    {
      key: "review",
      label: t("order:buyerSales.review"),
      allowed: canReview && ORD_SPLIT.hasAccess,
      reason: !ORD_SPLIT.hasAccess ? noPerm : canReview ? t("order:buyerOrders.detail.reviewOk") : t("order:buyerOrders.detail.reviewBlocked"),
      code: !ORD_SPLIT.hasAccess ? "PERM" : canReview ? null : "NOT_IN_STATUS",
      onClick: () => setActions(["review"]),
    },
    {
      key: "date",
      label: t("order:buyerOrders.detail.changeDate"),
      allowed: dateChangeStatus,
      code: dateChangeStatus ? null : delivering ? "STARTED" : "NOT_SENT",
      reason: dateChangeStatus
        ? t("order:buyerOrders.detail.changeDateOk")
        : delivering
          ? t("order:buyerOrders.detail.changeDateStarted")
          : t("order:buyerOrders.detail.changeDateNotSent"),
      onClick: () => setDateChangeOpened(true),
    },
    {
      key: "cancel",
      label: t("common:actions.cancel"),
      allowed: cancellable && ORD_CANCEL.hasAccess,
      reason: !ORD_CANCEL.hasAccess ? noPerm : cancellable ? t("order:buyerOrders.detail.cancelOk") : t("order:buyerOrders.detail.cancelBlocked"),
      code: !ORD_CANCEL.hasAccess ? "PERM" : cancellable ? null : "NOT_IN_STATUS",
      onClick: handleCancel,
    },
    {
      key: "return",
      label: t("buyerApp:entry.title"),
      allowed: !returnReason && RETURN_CREATE.hasAccess,
      reason: !RETURN_CREATE.hasAccess ? noPerm : returnReason || t("order:buyerOrders.detail.returnOk"),
      code: !RETURN_CREATE.hasAccess ? "PERM" : !returnReason ? null : order?.returnWindowExpiresAt ? "EXPIRED" : "NOT_DELIVERED",
      onClick: () => navigate(BUYER_RETURNS_ROUTES.orderReturn(order.id)),
    },
    ...(canApprove
      ? [{
        key: "respond",
        label: t("order:buyerOrders.detail.respond"),
        allowed: ORD_SPLIT.hasAccess,
        reason: ORD_SPLIT.hasAccess ? t("order:buyerOrders.detail.respondOk") : noPerm,
        code: ORD_SPLIT.hasAccess ? null : "PERM",
        onClick: () => setActions(["order"]),
        primary: true,
      }]
      : []),
  ]

  // ── History: the stamps the order carries, named like the progress steps ──
  const statusName = (code: string) => orderStatus?.find((c: any) => c.code === code)?.sentName || t(`order:orderStatus.${code}`)
  const history = [
    order?.canceledDate && { at: order.canceledDate, what: statusName("CANCELED"), who: "-" },
    order?.respondedDate && {
      at: order.respondedDate,
      what: status === "REJECTED" ? statusName("REJECTED") : statusName("AUTHORIZED"),
      who: renderName(order?.respondedUser) || "-",
    },
    order?.reviewedDate && { at: order.reviewedDate, what: statusName("REVIEWED"), who: renderName(order?.reviewedUser) || "-" },
    order?.createdAt && { at: order.createdAt, what: statusName("REGISTERED"), who: renderName(order?.orderedUser) || "-" },
  ].filter(Boolean) as { at: string; what: string; who: string }[]

  const reorderReason = reorderBlockedReason(order, t, reorderNetwork)
  const payReason = !order?.proformaInvoiceId
    ? t("order:buyerOrders.detail.payNotNeeded")
    : isPaid
      ? t("order:actions.paid")
      : null

  // Price source per line: the order line stores the winning price type in
  // priceGroupName (PROMOTION / SPECIAL / SEGMENT / BASE; gift lines carry FREE_GOODS).
  const priceSourceCell = (line: any) => {
    const group = String(line?.priceGroupName || "").toUpperCase()
    const labels: Record<string, string> = {
      PROMOTION: t("order:buyerOrders.detail.priceSrc.promotion"),
      SPECIAL: t("order:buyerOrders.detail.priceSrc.special"),
      SEGMENT: t("order:buyerOrders.detail.priceSrc.segment"),
      BASE: t("order:buyerOrders.detail.priceSrc.base"),
      FREE_GOODS: t("order:buyerOrders.detail.priceSrc.gift"),
    }
    if (!labels[group]) return <Text className={classes.meta}>-</Text>
    return (
      <Stack gap={2} align="flex-start">
        <Box component="span" className={pillClasses.pill}>
          {promoIcon(group === "BASE" ? "BASE" : group === "PROMOTION" ? "DISCOUNT" : group === "FREE_GOODS" ? "FREE_GOODS" : group)}
          {labels[group]}
        </Box>
        {group === "BASE" && <Text className={classes.meta}>{t("order:buyerOrders.detail.priceSrc.noPromo")}</Text>}
      </Stack>
    )
  }

  const stockCell = (line: any) => {
    const left = stockOf(line)
    const tone: Tone = left <= 0 ? "red" : left < 10 ? "amber" : "green"
    const text = left <= 0
      ? t("order:buyerOrders.detail.stockOut")
      : left < 10
        ? t("order:buyerOrders.detail.stockLow", { count: left })
        : t("order:buyerOrders.detail.stockOk", { count: left })
    return (
      <Group gap={6} wrap="nowrap">
        <Dot tone={tone} />
        <Text className={classes.nowrap}>{text}</Text>
      </Group>
    )
  }

  return (
    <>
      <Stack gap="md" className={classes.page}>
        {/* Header */}
        <Group justify="space-between" wrap="wrap" gap="sm">
          <Group gap="md" wrap="wrap">
            <Button variant="default" leftSection={<CaretLeftIcon size={16} />} onClick={() => navigate(isApp ? BUYER_RETURNS_ROUTES.hub : "/buyer-sales")}>
              {t("common:actions.back")}
            </Button>
            <Text className={classes.code} translate="no">
              {order?.purchaseCode || order?.salesCode || t("order:orderStatus.DRAFT")}
            </Text>
            <OrderStatusPill order={order} help={!isApp} />
            {order?.purchaseCode && order?.salesCode && (
              <Text className={classes.meta}>{t("order:buyerOrders.detail.supplierNo", { code: order.salesCode })}</Text>
            )}
          </Group>
          {!isApp && (
            <DisabledReason
              helpKey="order.buyer-sales-detail.reason.reorder"
              reason={!(ORD_SAVE.hasAccess || ORD_SEND.hasAccess) ? "PERM" : reorderReason ? "BLOCKED" : null}
              when={!(ORD_SAVE.hasAccess || ORD_SEND.hasAccess) || !!reorderReason}
              position="bottom-end"
            >
              <HelpButtonGroup helpKey="order.buyer-sales-detail.action.reorder">
                <Button data-help-id="action-reorder" variant="default" leftSection={<ArrowsClockwiseIcon size={16} />} onClick={() => addOrder(order)}>
                  {t("order:buyerOrders.reorder")}
                </Button>
              </HelpButtonGroup>
            </DisabledReason>
          )}
        </Group>

        {/* Progress */}
        {steps.length > 0 && (
          <Box className={classes.card}>
            <Box className={classes.steps}>
              {steps.map((step, i) => {
                const isFinal = currentStatus === "DELIVERED" && i === currentIndex && i === steps.length - 1
                const state = i < currentIndex || isFinal ? "done" : i === currentIndex ? "current" : "todo"
                return (
                  <Box key={step.code} className={classes.step} data-state={state} aria-current={state === "current" ? "step" : undefined}>
                    {/* @dehub/help step popover (R5) wraps the step's content, not the flex item, so the bar keeps its layout. */}
                    <StatusStepPopover help={isApp ? null : stepHelp(step.code)} title={step.name}>
                      <Box style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "var(--mantine-spacing-xs)" }}>
                        <Box className={classes.stepDot} data-state={state}>
                          {state === "done" && isApp ? <CheckIcon size={16} weight="bold" /> : i + 1}
                        </Box>
                        <Text className={classes.stepLabel} data-state={state}>
                          {step.name}
                        </Text>
                      </Box>
                    </StatusStepPopover>
                  </Box>
                )
              })}
            </Box>
          </Box>
        )}

        {/* Actions */}
        {!isApp && (
          <Card title={<>{t("common:actions.action")}<HelpIcon helpKey={`${HELP}.section.actions`} variant="section" /></>}>
            <SimpleGrid cols={{ base: 1, sm: 2, lg: tiles.length }} spacing="md">
              {tiles.map((tile) => (
                <Stack key={tile.key} gap={6} align="flex-start">
                  {/* @dehub/help: attached «?» (R2) + reason popover while unavailable (R4); the hint line stays. */}
                  <DisabledReason helpKey={`${HELP}.reason.${tile.key}`} reason={tile.code ?? null} when={!tile.allowed} position="bottom-start">
                    <HelpButtonGroup helpKey={`${HELP}.action.${tile.key}`}>
                      <Button
                        data-help-id={`action-${tile.key}`}
                        variant={tile.allowed && tile.primary ? "filled" : "default"}
                        color={tile.primary ? "brand" : undefined}
                        onClick={tile.onClick}
                      >
                        {tile.label}
                      </Button>
                    </HelpButtonGroup>
                  </DisabledReason>
                  <Text className={classes.hint}>{tile.reason}</Text>
                </Stack>
              ))}
            </SimpleGrid>
          </Card>
        )}

        <Grid gap="md" align="flex-start">
          {/* Left column */}
          <Grid.Col span={{ base: 12, lg: isApp ? 8 : 8 }}>
            <Stack gap="md">
              {!isApp && (
                <Box>
                  <DeliveryLocationMap
                    orderId={order?.id}
                    latitude={order?.deliveryLatitude}
                    longitude={order?.deliveryLongitude}
                    fallbackLat={order?.receiverBranch?.locationLat}
                    fallbackLng={order?.receiverBranch?.locationLng}
                    canEdit={canEditGps}
                    compactUnset
                    onSaved={reload}
                  />
                  {order?.id && <DeliveryTimeline orderId={order.id} />}
                </Box>
              )}

              <Card
                flush
                title={<>{t("order:buyerSales.products")}{!isApp && <HelpIcon helpKey={`${HELP}.section.products`} variant="section" />}</>}
                extra={<Text className={classes.meta}>{t("order:buyerOrders.detail.linesSummary", { types: lines.length, qty: totalQuantity })}</Text>}
              >
                <Box className={classes.scroll}>
                  <Table className={classes.table} verticalSpacing={0} horizontalSpacing={0}>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>{t("order:buyerOrders.detail.item")}</Table.Th>
                        <Table.Th>{t("order:buyerOrders.detail.priceSource")}</Table.Th>
                        <Table.Th className={classes.right}>{t("order:buyerOrders.detail.qtyPrice")}</Table.Th>
                        <Table.Th className={classes.right}>{t("order:buyerOrders.detail.amount")}</Table.Th>
                        {!isApp && <Table.Th>{t("order:buyerSales.remaining")}</Table.Th>}
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {lines.map((item: any, index: number) => (
                        <Table.Tr key={item?.id || index}>
                          <Table.Td>
                            <Group gap="sm" wrap="nowrap">
                              <Avatar src={item?.image || null} size={44} radius="md" className={classes.thumb}>
                                <ImageIcon size={18} />
                              </Avatar>
                              <Box miw={0}>
                                <Text className={classes.lineName}>{item?.name || "-"}</Text>
                                <Text className={classes.mono} translate="no">{item?.skuCode || "-"}</Text>
                              </Box>
                            </Group>
                          </Table.Td>
                          <Table.Td>{priceSourceCell(item)}</Table.Td>
                          <Table.Td className={`${classes.mono} ${classes.right}`}>
                            {`${Number(item?.quantity) || 0} x ${currencyFormat(Number(item?.price) || 0)}`}
                          </Table.Td>
                          <Table.Td className={`${classes.mono} ${classes.right}`} fw={isApp ? 700 : undefined}>
                            {currencyFormat((Number(item?.quantity) || 0) * (Number(item?.price) || 0))}
                          </Table.Td>
                          {!isApp && <Table.Td>{stockCell(item)}</Table.Td>}
                        </Table.Tr>
                      ))}
                      {lines.length === 0 && (
                        <Table.Tr>
                          <Table.Td colSpan={isApp ? 4 : 5} ta="center" c="var(--nw-text-2)">
                            {t("order:buyerSales.noProducts")}
                          </Table.Td>
                        </Table.Tr>
                      )}
                    </Table.Tbody>
                  </Table>
                </Box>
              </Card>

              {/* Path-A return entry (screens-buyer-app §1, canvas OrdB_AppOrder) */}
              {isApp && order?.isReturnEligible !== undefined && (
                <OrderReturnEntry order={order as IReturnOrderDetail} canCreate={RETURN_CREATE.hasAccess} />
              )}

              {(order?.senderNote || order?.senderAdditionalNote) && (
                <Card title={<>{t("order:buyerSales.notes")}{!isApp && <HelpIcon helpKey={`${HELP}.section.notes`} variant="section" />}</>}>
                  <Stack gap="xs">
                    {order?.senderNote && <Row label={t("order:buyerSales.toPartner")} value={order.senderNote} />}
                    {order?.senderAdditionalNote && <Row label={t("order:buyerSales.internalNote")} value={order.senderAdditionalNote} />}
                  </Stack>
                </Card>
              )}

              {!isApp && history.length > 0 && (
                <Card title={<>{t("order:buyerOrders.detail.history")}<HelpIcon helpKey={`${HELP}.section.history`} variant="section" /></>}>
                  <Stack gap={8}>
                    {history.map((h, i) => (
                      <Box key={i} className={classes.historyRow}>
                        <Text className={classes.mono}>{dateTimeSecFormat(h.at)}</Text>
                        <Text fw={600}>{h.what}</Text>
                        <Text c="var(--nw-text-2)">{h.who}</Text>
                      </Box>
                    ))}
                  </Stack>
                </Card>
              )}
            </Stack>
          </Grid.Col>

          {/* Right column */}
          <Grid.Col span={{ base: 12, lg: 4 }}>
            <Stack gap="md">
              <Card title={<>{t("order:buyerSales.supplier")}{!isApp && <HelpIcon helpKey={`${HELP}.section.supplier`} variant="section" />}</>}>
                <Text className={classes.supplierName}>{renderBusinessName(supplier)}</Text>
                <Text className={classes.meta}>
                  {t("order:buyerSales.taxId")} {supplier?.regNumber || "-"}
                </Text>
                {!isApp && (
                  <Text className={classes.meta}>
                    {t("order:buyerSales.manager")}: {renderName(order?.supplierStaff) || "-"}
                  </Text>
                )}
              </Card>

              <Card title={<>{t("order:buyerSales.delivery")}{!isApp && <HelpIcon helpKey={`${HELP}.section.delivery`} variant="section" />}</>}>
                <Stack gap={6}>
                  {order?.deliveredAt && <Row label={t("order:buyerOrders.detail.deliveredAt")} value={dateFormat(order.deliveredAt)} />}
                  <Row
                    label={order?.deliveredAt ? t("order:buyerOrders.detail.plannedDate") : t("order:buyerOrders.deliveryDay")}
                    value={order?.deliveryDate ? dateFormat(order.deliveryDate) : "-"}
                  />
                  {isApp ? (
                    <Row label={t("order:buyerSales.registered")} value={order?.createdAt ? dateTimeSecFormat(order.createdAt) : "-"} />
                  ) : (
                    <>
                      <Row label={t("order:buyerSales.receiverBranch")} value={order?.receiverBranch?.name} />
                      <Row label={t("order:buyerSales.address")} value={order?.receiverBranch?.branchAddress} />
                      <Row label={t("order:buyerSales.receiverStaff")} value={renderName(order?.receiverStaff)} />
                    </>
                  )}
                </Stack>
              </Card>

              <Card title={<>{t("order:buyerSales.payment")}{!isApp && <HelpIcon helpKey={`${HELP}.section.payment`} variant="section" />}</>}>
                <Stack gap={6}>
                  {!isApp && <Row label={t("order:buyerSales.paymentTerm")} value={order?.paymentTerm?.description} />}
                  {!isApp && <Row label={t("order:buyerSales.totalQty")} value={`${totalQuantity} ${t("order:buyerSales.pcs")}`} />}
                  <Row label={t("order:buyerOrders.detail.goodsAmount")} value={currencyFormat(orderAmount || totalAmount)} />
                  {headerDiscount > 0 && <Row label={discountLabel} value={`-${currencyFormat(headerDiscount)}`} />}
                  <Row label={t("order:buyerSales.delivery")} value={currencyFormat(shippingAmount)} />
                  <Row label={t("order:buyerOrders.detail.vatIncluded")} value={currencyFormat(Number(order?.lineVatAmount) || 0)} />
                  <Box className={classes.totalRow}>
                    <Text fw={600}>{t("order:buyerSales.grandTotal")}</Text>
                    <Text className={classes.grand}>{currencyFormat(totalAmount)}</Text>
                  </Box>
                </Stack>
              </Card>

              {!isApp && (
                <Card title={<>{t("order:buyerSales.invoice")}<HelpIcon helpKey={`${HELP}.section.invoice`} variant="section" /></>}>
                  {invoiceId ? (
                    <Stack gap={6}>
                      <Row label={t("order:buyerSales.invoiceNo")} value={invoice?.refCode} />
                      <Row
                        label={t("order:buyerSales.status")}
                        value={
                          invoice?.invoiceStatus
                            ? [
                              t(`order:buyerOrders.invoiceStatus.${invoice.invoiceStatus}`, { defaultValue: invoice.invoiceStatus }),
                              invoice?.paymentStatus ? t(`order:buyerOrders.paymentStatus.${invoice.paymentStatus}`, { defaultValue: invoice.paymentStatus }) : "",
                            ].filter(Boolean).join(", ")
                            : "-"
                        }
                      />
                      <Row
                        label={t("order:buyerSales.paymentDue")}
                        value={invoice?.paymentDate ? dateFormat(invoice.paymentDate) : order?.paymentTerm?.description}
                      />
                      <ReasonTooltip reason={payReason || undefined}>
                        <Button data-help-id="action-payment" fullWidth mt="xs" color="brand" leftSection={<CheckIcon size={16} />} onClick={() => setActions(["payment"])}>
                          {t("order:buyerSales.paymentConfirm")}
                        </Button>
                      </ReasonTooltip>
                    </Stack>
                  ) : (
                    <Text className={classes.hint}>{t("order:buyerOrders.detail.noInvoice")}</Text>
                  )}
                </Card>
              )}
            </Stack>
          </Grid.Col>
        </Grid>
      </Stack>

      <DeliveryDateChangeRequestModal
        opened={dateChangeOpened}
        onClose={() => setDateChangeOpened(false)}
        orderId={order?.id}
        currentDeliveryDate={order?.deliveryDate}
        onDone={reload}
      />

      <Modal opened={actions[0] === "review"} onClose={() => setActions([])} withCloseButton={false} size="600px" padding={0} centered radius="lg">
        <BuyerSalesDynamicReviewForm action={order} setActions={setActions} reload={reload} />
      </Modal>

      <Modal opened={actions[0] === "order"} onClose={() => setActions([])} withCloseButton={false} size="600px" padding={0} centered radius="lg">
        <BuyerSalesDynamicConfirmForm action={order} setActions={setActions} reload={reload} />
      </Modal>

      <Modal opened={actions[0] === "payment"} onClose={() => setActions([])} size={1240} withCloseButton={false} padding={0} centered radius="lg">
        <BuyerSalesPaymentForm action={["PAYMENT", order]} setAction={setActions} reload={reload} />
      </Modal>
    </>
  )
}
